import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { setIsCartOpen, setIsSearchOpen, cart } = useApp();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Kits', href: '#kits' },
    { name: 'System', href: '#system' },
    { name: 'Journal', href: '#journal' },
  ];

  return (
    <>
      <motion.header 
        className={`fixed w-full z-50 transition-all duration-500 ${
          isScrolled || mobileMenuOpen ? 'bg-parchment/95 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-6'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-12">
            <a href="#" className="font-serif text-2xl md:text-3xl font-bold tracking-tighter text-cobalt flex items-center gap-2 group">
              <SafeIcon icon={FiIcons.FiCompass} className="text-amber group-hover:rotate-45 transition-transform duration-500" />
              <span className="hidden sm:inline">NomadCrate</span>
            </a>
            
            <nav className="hidden lg:flex gap-8 text-[11px] font-bold tracking-[0.2em] uppercase text-ink-light">
              {navLinks.map(link => (
                <a key={link.name} href={link.href} className="hover:text-amber transition-colors relative group">
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-amber transition-all group-hover:w-full"></span>
                </a>
              ))}
            </nav>
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <button 
              onClick={() => setIsSearchOpen(true)}
              className="text-cobalt hover:text-amber transition-colors p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber rounded-full"
              aria-label="Search Archive"
            >
              <SafeIcon icon={FiIcons.FiSearch} className="text-xl" />
            </button>
            <button 
              onClick={() => setIsCartOpen(true)}
              className="relative text-cobalt hover:text-amber transition-colors group p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber rounded-full"
              aria-label={`View Manifest with ${cart.length} items`}
            >
              <SafeIcon icon={FiIcons.FiShoppingBag} className="text-xl" />
              {cart.length > 0 && (
                <motion.span 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-1 right-1 bg-amber text-white text-[8px] font-black h-3.5 w-3.5 rounded-full flex items-center justify-center shadow-sm"
                >
                  {cart.reduce((acc, i) => acc + i.quantity, 0)}
                </motion.span>
              )}
            </button>
            <button 
              className="lg:hidden text-cobalt p-2 focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close Menu" : "Open Menu"}
            >
              <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="text-2xl" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-40 bg-parchment pt-24 px-6 lg:hidden"
          >
            <div className="flex flex-col gap-8">
              {navLinks.map((link, i) => (
                <motion.a
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  key={link.name}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-4xl font-serif font-bold text-cobalt border-b border-cobalt/10 pb-4"
                >
                  {link.name}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;